export interface Team {
  id: string;
  name: string;
  seed_number: number;
}

export interface Match {
  id?: string;
  tournament_id: string;
  round_number: number;
  match_number: number;
  bracket_type: 'winners' | 'losers' | 'pool' | 'final';
  team1_id?: string;
  team2_id?: string;
  team1_name?: string;
  team2_name?: string;
  status: 'scheduled' | 'in_progress' | 'completed';
  next_match_id?: string;
  next_match_slot?: number;
  loser_next_match_id?: string;
  loser_next_match_slot?: number;
  pool_id?: string;
  pool_number?: number;
}

export interface BracketStructure {
  matches: Match[];
  rounds: number;
  totalMatches: number;
}

export class BracketGenerator {
  
  static generateSingleElimination(tournamentId: string, teams: Team[]): BracketStructure {
    const matches: Match[] = [];
    const teamCount = teams.length;
    const rounds = Math.ceil(Math.log2(teamCount));
    let matchNumber = 1;

    // Sort teams by seed number
    const sortedTeams = [...teams].sort((a, b) => a.seed_number - b.seed_number);
    
    // Calculate bracket size (next power of 2)
    const bracketSize = Math.pow(2, rounds);
    const byes = bracketSize - teamCount;

    // First round
    let currentRoundTeams = [...sortedTeams];
    
    // Add byes (represented as null teams)
    for (let i = 0; i < byes; i++) {
      currentRoundTeams.splice(i * 2 + 1, 0, null);
    }

    for (let round = 1; round <= rounds; round++) {
      const roundMatches: Match[] = [];
      const nextRoundTeams: (Team | null)[] = [];

      if (round === 1) {
        // First round with possible byes
        for (let i = 0; i < currentRoundTeams.length; i += 2) {
          const team1 = currentRoundTeams[i];
          const team2 = currentRoundTeams[i + 1];

          if (team1 && team2) {
            // Regular match
            roundMatches.push({
              tournament_id: tournamentId,
              round_number: round,
              match_number: matchNumber++,
              bracket_type: 'winners',
              team1_id: team1.id,
              team2_id: team2.id,
              team1_name: team1.name,
              team2_name: team2.name,
              status: 'scheduled'
            });
            nextRoundTeams.push(null); // Winner TBD
          } else if (team1 && !team2) {
            // Bye - team1 advances automatically
            nextRoundTeams.push(team1);
          }
        }
      } else {
        // Subsequent rounds
        for (let i = 0; i < currentRoundTeams.length; i += 2) {
          roundMatches.push({
            tournament_id: tournamentId,
            round_number: round,
            match_number: matchNumber++,
            bracket_type: round === rounds ? 'final' : 'winners',
            status: 'scheduled'
          });
          nextRoundTeams.push(null); // Winner TBD
        }
      }

      matches.push(...roundMatches);
      currentRoundTeams = nextRoundTeams;
    }

    return {
      matches,
      rounds,
      totalMatches: matches.length
    };
  }

  static generateDoubleElimination(tournamentId: string, teams: Team[]): BracketStructure {
    const matches: Match[] = [];
    const teamCount = teams.length;
    const winnerRounds = Math.ceil(Math.log2(teamCount));
    const loserRounds = (winnerRounds - 1) * 2;
    let matchNumber = 1;

    // Generate winners bracket (same as single elimination)
    const winnersMatches = this.generateSingleElimination(tournamentId, teams).matches;
    
    // Update bracket_type for winners bracket
    winnersMatches.forEach(match => {
      match.match_number = matchNumber++;
      match.bracket_type = match.bracket_type === 'final' ? 'winners' : 'winners';
    });
    
    matches.push(...winnersMatches);

    // Generate losers bracket
    for (let round = 1; round <= loserRounds; round++) {
      const isEliminationRound = round % 2 === 0;
      const matchesInRound = isEliminationRound ? 
        Math.pow(2, winnerRounds - Math.ceil(round / 2) - 1) :
        Math.pow(2, winnerRounds - Math.ceil(round / 2));

      for (let i = 0; i < matchesInRound; i++) {
        matches.push({
          tournament_id: tournamentId,
          round_number: round,
          match_number: matchNumber++,
          bracket_type: 'losers',
          status: 'scheduled'
        });
      }
    }

    // Grand final
    matches.push({
      tournament_id: tournamentId,
      round_number: winnerRounds + loserRounds + 1,
      match_number: matchNumber++,
      bracket_type: 'final',
      status: 'scheduled'
    });

    return {
      matches,
      rounds: winnerRounds + loserRounds + 1,
      totalMatches: matches.length
    };
  }

  static generateRoundRobin(tournamentId: string, teams: Team[]): BracketStructure {
    const matches: Match[] = [];
    const teamCount = teams.length;
    let matchNumber = 1;

    // Sort teams by seed number
    const sortedTeams = [...teams].sort((a, b) => a.seed_number - b.seed_number);

    // Generate all possible matchups
    for (let i = 0; i < sortedTeams.length; i++) {
      for (let j = i + 1; j < sortedTeams.length; j++) {
        matches.push({
          tournament_id: tournamentId,
          round_number: 1, // All matches are in round 1 for round robin
          match_number: matchNumber++,
          bracket_type: 'pool',
          team1_id: sortedTeams[i].id,
          team2_id: sortedTeams[j].id,
          team1_name: sortedTeams[i].name,
          team2_name: sortedTeams[j].name,
          status: 'scheduled'
        });
      }
    }

    return {
      matches,
      rounds: 1,
      totalMatches: matches.length
    };
  }

  static generatePoolPlayPlusPlayoffs(tournamentId: string, teams: Team[]): BracketStructure {
    const matches: Match[] = [];
    const teamCount = teams.length;
    let matchNumber = 1;

    // Sort teams by seed number
    const sortedTeams = [...teams].sort((a, b) => a.seed_number - b.seed_number);
    
    // Determine pool size (4 teams per pool is optimal)
    const poolSize = 4;
    const poolCount = Math.ceil(teamCount / poolSize);
    
    // Distribute teams into pools (snake draft style for balance)
    const pools: Team[][] = Array.from({ length: poolCount }, () => []);
    
    for (let i = 0; i < teamCount; i++) {
      const poolIndex = Math.floor(i / poolSize);
      if (poolIndex < poolCount) {
        pools[poolIndex].push(sortedTeams[i]);
      }
    }

    // Generate pool play matches with pool_id
    const poolLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    pools.forEach((pool, poolIndex) => {
      const poolId = `pool_${poolLetters[poolIndex] || poolIndex + 1}`;
      const poolNumber = poolIndex + 1;
      
      for (let i = 0; i < pool.length; i++) {
        for (let j = i + 1; j < pool.length; j++) {
          matches.push({
            tournament_id: tournamentId,
            round_number: 1,
            match_number: matchNumber++,
            bracket_type: 'pool',
            team1_id: pool[i].id,
            team2_id: pool[j].id,
            team1_name: pool[i].name,
            team2_name: pool[j].name,
            status: 'scheduled',
            pool_id: poolId,
            pool_number: poolNumber,
          });
        }
      }
    });

    // Generate playoff bracket (top 2 from each pool)
    const playoffTeamCount = poolCount * 2;
    const playoffRounds = Math.ceil(Math.log2(playoffTeamCount));
    
    for (let round = 2; round <= playoffRounds + 1; round++) {
      const matchesInRound = Math.pow(2, playoffRounds - (round - 2));
      
      for (let i = 0; i < matchesInRound; i++) {
        matches.push({
          tournament_id: tournamentId,
          round_number: round,
          match_number: matchNumber++,
          bracket_type: round === playoffRounds + 1 ? 'final' : 'winners',
          status: 'scheduled'
        });
      }
    }

    return {
      matches,
      rounds: playoffRounds + 1,
      totalMatches: matches.length
    };
  }

  static generateBracket(tournamentType: string, tournamentId: string, teams: Team[]): BracketStructure {
    switch (tournamentType) {
      case 'single_elimination':
        return this.generateSingleElimination(tournamentId, teams);
      case 'double_elimination':
        return this.generateDoubleElimination(tournamentId, teams);
      case 'round_robin':
        return this.generateRoundRobin(tournamentId, teams);
      case 'pool_play_playoffs':
        return this.generatePoolPlayPlusPlayoffs(tournamentId, teams);
      default:
        throw new Error(`Unsupported tournament type: ${tournamentType}`);
    }
  }

  /**
   * Link bracket matches by setting next_match_id and next_match_slot
   * Must be called AFTER matches are inserted and have database IDs
   */
  static linkBracketMatches(
    matches: Match[], 
    tournamentType: string
  ): { matchId: string; nextMatchId: string; nextMatchSlot: number; loserNextMatchId?: string; loserNextMatchSlot?: number }[] {
    const links: { matchId: string; nextMatchId: string; nextMatchSlot: number; loserNextMatchId?: string; loserNextMatchSlot?: number }[] = [];

    if (tournamentType === 'single_elimination') {
      links.push(...this.linkSingleEliminationMatches(matches));
    } else if (tournamentType === 'double_elimination') {
      links.push(...this.linkDoubleEliminationMatches(matches));
    } else if (tournamentType === 'pool_play_playoffs') {
      // Link only the playoff portion (winners bracket matches)
      const playoffMatches = matches.filter(m => m.bracket_type === 'winners' || m.bracket_type === 'final');
      links.push(...this.linkSingleEliminationMatches(playoffMatches));
    }
    // Round robin doesn't need linking - no advancement

    return links;
  }

  private static linkSingleEliminationMatches(matches: Match[]): { matchId: string; nextMatchId: string; nextMatchSlot: number; loserNextMatchId?: string; loserNextMatchSlot?: number }[] {
    const links: { matchId: string; nextMatchId: string; nextMatchSlot: number; loserNextMatchId?: string; loserNextMatchSlot?: number }[] = [];

    // Filter to only winners and final bracket types
    const bracketMatches = matches.filter(m => 
      m.bracket_type === 'winners' || m.bracket_type === 'final'
    );

    // Group matches by round
    const roundGroups = new Map<number, Match[]>();
    bracketMatches.forEach(match => {
      const round = match.round_number;
      if (!roundGroups.has(round)) {
        roundGroups.set(round, []);
      }
      roundGroups.get(round)!.push(match);
    });

    // Sort rounds
    const rounds = Array.from(roundGroups.keys()).sort((a, b) => a - b);

    // Sort matches within each round by match_number
    rounds.forEach(round => {
      roundGroups.get(round)!.sort((a, b) => a.match_number - b.match_number);
    });

    console.log('[BRACKET_LINK] Rounds:', rounds);
    console.log('[BRACKET_LINK] Round groups:', Array.from(roundGroups.entries()).map(([r, m]) => `Round ${r}: ${m.length} matches`));

    // Link each round to the next
    for (let i = 0; i < rounds.length - 1; i++) {
      const currentRound = roundGroups.get(rounds[i])!;
      const nextRound = roundGroups.get(rounds[i + 1])!;

      console.log(`[BRACKET_LINK] Linking round ${rounds[i]} (${currentRound.length} matches) to round ${rounds[i+1]} (${nextRound.length} matches)`);

      currentRound.forEach((match, index) => {
        const nextMatchIndex = Math.floor(index / 2);
        const nextMatch = nextRound[nextMatchIndex];

        if (nextMatch && match.id && nextMatch.id) {
          const slot = (index % 2 === 0) ? 1 : 2;
          links.push({
            matchId: match.id,
            nextMatchId: nextMatch.id,
            nextMatchSlot: slot
          });
          console.log(`[BRACKET_LINK] Match ${match.match_number} (${match.id}) -> Match ${nextMatch.match_number} (${nextMatch.id}), slot ${slot}`);
        }
      });
    }

    return links;
  }

  private static linkDoubleEliminationMatches(matches: Match[]): { matchId: string; nextMatchId: string; nextMatchSlot: number; loserNextMatchId?: string; loserNextMatchSlot?: number }[] {
    const links: { matchId: string; nextMatchId: string; nextMatchSlot: number; loserNextMatchId?: string; loserNextMatchSlot?: number }[] = [];

    // Separate winners and losers brackets
    const winnersMatches = matches.filter(m => m.bracket_type === 'winners');
    const losersMatches = matches.filter(m => m.bracket_type === 'losers');
    const finalMatch = matches.find(m => m.bracket_type === 'final');

    // Link winners bracket (same as single elimination)
    const winnersLinks = this.linkSingleEliminationMatches(winnersMatches);
    
    // For double elimination, winners bracket matches also send losers to losers bracket
    // Group losers matches by round
    const losersRoundGroups = new Map<number, Match[]>();
    losersMatches.forEach(match => {
      const round = match.round_number;
      if (!losersRoundGroups.has(round)) {
        losersRoundGroups.set(round, []);
      }
      losersRoundGroups.get(round)!.push(match);
    });

    // Sort losers matches within each round
    losersRoundGroups.forEach(roundMatches => {
      roundMatches.sort((a, b) => a.match_number - b.match_number);
    });

    // Add loser routing for winners bracket matches
    winnersLinks.forEach((link, index) => {
      const winnersMatch = winnersMatches.find(m => m.id === link.matchId);
      if (winnersMatch) {
        // First round losers go to losers bracket round 1
        // Later round losers go to appropriate losers bracket rounds
        const losersRound = winnersMatch.round_number;
        const losersRoundMatches = losersRoundGroups.get(losersRound);
        
        if (losersRoundMatches && losersRoundMatches.length > 0) {
          const loserMatchIndex = Math.floor(index / 2) % losersRoundMatches.length;
          const loserMatch = losersRoundMatches[loserMatchIndex];
          
          if (loserMatch?.id) {
            link.loserNextMatchId = loserMatch.id;
            link.loserNextMatchSlot = (index % 2 === 0) ? 1 : 2;
          }
        }
      }
    });

    links.push(...winnersLinks);

    // Link losers bracket matches to each other
    const losersRounds = Array.from(losersRoundGroups.keys()).sort((a, b) => a - b);
    for (let i = 0; i < losersRounds.length - 1; i++) {
      const currentRound = losersRoundGroups.get(losersRounds[i])!;
      const nextRound = losersRoundGroups.get(losersRounds[i + 1])!;

      currentRound.forEach((match, index) => {
        const nextMatchIndex = Math.floor(index / 2);
        const nextMatch = nextRound[nextMatchIndex];

        if (nextMatch && match.id && nextMatch.id) {
          links.push({
            matchId: match.id,
            nextMatchId: nextMatch.id,
            nextMatchSlot: (index % 2 === 0) ? 1 : 2
          });
        }
      });
    }

    // Link last losers bracket match to grand final
    if (finalMatch?.id && losersRounds.length > 0) {
      const lastLosersRound = losersRoundGroups.get(losersRounds[losersRounds.length - 1]);
      if (lastLosersRound && lastLosersRound.length > 0) {
        const lastLosersMatch = lastLosersRound[0];
        if (lastLosersMatch?.id) {
          links.push({
            matchId: lastLosersMatch.id,
            nextMatchId: finalMatch.id,
            nextMatchSlot: 2 // Losers bracket winner goes to slot 2 of final
          });
        }
      }

      // Last winners bracket match goes to final slot 1
      const winnersRoundGroups = new Map<number, Match[]>();
      winnersMatches.forEach(match => {
        const round = match.round_number;
        if (!winnersRoundGroups.has(round)) {
          winnersRoundGroups.set(round, []);
        }
        winnersRoundGroups.get(round)!.push(match);
      });
      const winnersRounds = Array.from(winnersRoundGroups.keys()).sort((a, b) => a - b);
      if (winnersRounds.length > 0) {
        const lastWinnersRound = winnersRoundGroups.get(winnersRounds[winnersRounds.length - 1]);
        if (lastWinnersRound && lastWinnersRound.length > 0) {
          const lastWinnersMatch = lastWinnersRound[0];
          if (lastWinnersMatch?.id) {
            links.push({
              matchId: lastWinnersMatch.id,
              nextMatchId: finalMatch.id,
              nextMatchSlot: 1 // Winners bracket winner goes to slot 1 of final
            });
          }
        }
      }
    }

    return links;
  }
}
